import { useCallback } from 'react';

import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { useSettings } from '@/data/Settings';
import { getExternalId } from '@/data/utils/externalId';
import { FacebookClientPayload } from '@/pages/api/tracking/facebook/facebookAPIUtils';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { useFacebookClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { triggerFacebookCAPIEvent } from 'presentation/events/services/facebookService';
import { safeDecodeUrl } from 'presentation/events/utils/ga4Utils';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';
import { emptyToNull } from 'presentation/events/utils/stringUtils';
import { useGetCategories } from 'shared/custom/hooks/Categories';

import { SendMediaEventProps, TrackViewItemProduct } from './types';
import { calculateProductsValue, getProductCategoryString, getViewItemProductData } from './utils';

export const useTrackViewItemFacebook = () => {
	const { settings } = useSettings();
	const { navigation } = useGetCategories();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const storeLocation = useTrackingStoreLocation();
	const { fbc, fbp } = useFacebookClickIdentifiers();

	const { userData, storeId } = settings;

	const sendFacebookEvent = useCallback(
		({ eventId, products, bundleId, personInfo }: SendMediaEventProps) => {
			if (userData?.ENABLE_CAPI_TARGET_CALL !== 'true') return;
			if (!physicalStoreId) return;

			const isBundle = !!bundleId;
			const contentIds = products.map((product) => getItemPartNumber(product));
			const value = calculateProductsValue(products, physicalStoreId);
			const singleProduct = products[0] as TrackViewItemProduct;

			const contents = !isBundle
				? undefined
				: products.map((product) => {
						const {
							id,
							quantity,
							price,
							categoryString: category,
						} = getViewItemProductData(product, physicalStoreId, navigation);

						return { id, quantity, item_price: price, category, brand: product.manufacturer };
				  });

			const singleProductData = isBundle
				? {}
				: {
						content_name: singleProduct.name,
						content_category: getProductCategoryString(singleProduct, navigation),
				  };

			const facebookPayload: FacebookClientPayload = {
				event_name: 'ViewContent',
				event_id: eventId,
				storeId,
				event_source_url: safeDecodeUrl(window.location.href),
				fbc,
				fbp,
				test_event_code: userData?.FACEBOOK_TEST_CODE,
				custom_data: {
					content_ids: contentIds,
					contents,
					...singleProductData,
					content_type: 'product',
					currency: DEFAULT_CURRENCY,
					value,
					email: emptyToNull(personInfo?.email),
					externalId: getExternalId(),
					firstName: emptyToNull(personInfo?.firstName),
					lastName: emptyToNull(personInfo?.lastName ?? null),
					city: emptyToNull(storeLocation?.city ?? null),
					state: emptyToNull(storeLocation?.state ?? null),
					phone: emptyToNull(personInfo?.phone ?? null),
					zipCode: emptyToNull(storeLocation?.zipCode ?? null),
				},
			};

			triggerFacebookCAPIEvent(facebookPayload);
		},
		[
			userData?.ENABLE_CAPI_TARGET_CALL,
			userData?.FACEBOOK_TEST_CODE,
			physicalStoreId,
			storeLocation,
			storeId,
			fbc,
			fbp,
			navigation,
		]
	);

	return { sendFacebookEvent };
};
